/**
 * packages/dados/src/rascunho.ts — o salvamento automático.
 *
 * O QUE ISTO É
 *   Enquanto o usuário digita, nada vai para o cenário publicado. O estado da edição é
 *   gravado em `app.cenario_rascunho`: um documento jsonb POR USUÁRIO por cenário, que
 *   NÃO é auditado (decisão D-07 do plano — é o que tira a colagem de 300 células do
 *   GRID da trilha de auditoria).
 *
 * O QUE ISTO NÃO É
 *   ⚠ Este arquivo NUNCA escreve em `app.cenario` nem em nenhuma filha. Publicar é o
 *   botão "Salvar", que vive em `repositorios.publicarCenario()`. A única tabela tocada
 *   aqui é a constante `TABELA`, e ela não muda.
 *
 * COM A CONEXÃO CAÍDA (decisão do usuário, 26/07/2026)
 *   O motor roda no navegador: o cálculo continua e a edição NÃO é bloqueada. O que muda
 *   é o aviso — e quem dá o aviso é o `conexao.ts`, dono da barra de status. Este módulo
 *   se registra em `conexao.aoReconectar()`; quando a rede volta, o monitor chama a
 *   rotina sozinho e o rascunho sobe sem o usuário tocar em nada.
 *
 * DIVISÃO COM O `conexao.ts` (uma fonte de verdade para cada coisa)
 *   · conexao.ts → rede, situação do salvamento, frase de tela, agenda de retentativa.
 *   · rascunho.ts → o CONTEÚDO pendente, o agrupamento das edições e a gravação.
 *   A tela observa `conexao.assinar(...)`; não há um segundo estado paralelo aqui.
 */

import { ErroFincast, obterCliente } from './cliente';
import { conexao } from './conexao';
import type { EstadoConexao } from './conexao';
import { obterPerfil, aoMudarAutenticacao } from './sessao';
import { traduzirErro } from './repositorios';
import type { EntradaMotor } from './mapeamento';
import type { Json } from './banco.tipos';

/** A ÚNICA tabela que este módulo escreve. Não é auditada e não é o cenário publicado. */
const TABELA = 'cenario_rascunho' as const;

/** Agrupa rajadas de digitação/colagem numa gravação só. */
const ESPERA_MS = 800;
/** Teto do adiamento: digitação contínua não pode adiar a gravação para sempre. */
const ADIAMENTO_MAXIMO_MS = 5_000;
/**
 * Marca o formato do documento — o dia em que o formato mudou (0032, cadastro por cenário), foi
 * isto que salvou. O pacote GRAVA só o formato 2; o banco recusa gravar o 1 depois da 0032
 * (CHECK NOT VALID `cenario_rascunho_formato_2`), e os rascunhos do formato 1 continuam LIDOS.
 */
const FORMATO = 'fincast/rascunho/2' as const;
/** O formato de antes da 0032: só lido, nunca gravado. */
const FORMATO_1 = 'fincast/rascunho/1' as const;

/* =============================================================================
 * Tipos
 * ========================================================================== */

export interface RascunhoCarregado {
  cenarioId: string;
  /** `{empreendimento, lev}` exatamente como o usuário deixou. */
  entrada: EntradaMotor;
  /** Versão do cenário publicado sobre a qual este rascunho foi feito. */
  baseVersao: number;
  atualizadoEm: string;
  /**
   * 2 = gravado depois da 0032: o cadastro é do cenário e o rascunho vence em tudo (menos o nome).
   * 1 = gravado ANTES da 0032: vale ainda a regra do LGC do lote 4 (D25 = A, 12/10/2026), que
   * separa o LGC digitado da fotografia — até a 0033.
   */
  formato: 1 | 2;
  /**
   * SÓ no formato 1 (revisão do lote 4, 10/10/2026): o "LGC disponível" OFICIAL quando a sessão
   * que gravou o rascunho começou — com ela a tela sabe se o LGC do rascunho foi DIGITADO
   * (diferente dela) ou é só a fotografia (igual a ela). Ausente nos rascunhos mais antigos.
   */
  lgcOficialNaOrigem?: number;
}

export interface SituacaoRascunho {
  /** Cenário com sessão de rascunho aberta. */
  cenarioId: string | null;
  /** Há conteúdo digitado que o servidor ainda não confirmou. */
  pendente: boolean;
  /** Rede, salvamento e a frase de tela — vêm do monitor, não daqui. */
  conexao: EstadoConexao;
}

interface Sessao {
  cenarioId: string;
  empresaId: string;
  baseVersao: number;
}

/** Um item da fila é uma sessão + o conteúdo. Autossuficiente de propósito. */
interface ItemFila extends Sessao {
  entrada: EntradaMotor;
}

/** O que se grava (formato 2). */
interface Documento {
  formato: typeof FORMATO;
  entrada: EntradaMotor;
}

/** O que se lê: o formato 2, ou o 1 de antes da 0032 (que podia trazer a origem do LGC). */
interface DocumentoLido {
  formato: typeof FORMATO | typeof FORMATO_1;
  entrada: EntradaMotor;
  lgcOficialNaOrigem?: unknown;
}

const numeroFinito = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** O mínimo do `document` que usamos — o tsconfig não carrega a lib "DOM". */
interface DocumentoMinimo {
  visibilityState?: string;
  addEventListener(tipo: string, ouvinte: () => void): void;
}

/* =============================================================================
 * Estado do módulo (um cenário aberto por vez)
 * ========================================================================== */

let sessao: Sessao | null = null;
/**
 * Conteúdo digitado ainda não confirmado, **indexado por cenário**.
 *
 * Era uma variável só (`pendente: EntradaMotor | null`), e isso causava dois defeitos
 * que a revisão adversarial reproduziu:
 *
 *   · CONTAMINAÇÃO — gravação de A em voo, usuário troca para B, a chamada falha: o
 *     conteúdo de A voltava para a fila e era gravado com a sessão de B, ou seja
 *     `update cenario_rascunho set conteudo=<A> where cenario_id=B`. Depois disso,
 *     abrir B mostrava o trabalho de A.
 *   · PERDA — reabrir o MESMO cenário (remontagem da tela, recarga depois de publicar)
 *     zerava o pendente sem nenhuma ida ao servidor.
 *
 * Cada item carrega a SUA empresa e a SUA versão-base, então drenar não depende de qual
 * cenário está aberto. É seguro no banco: a chave é `(cenario_id, usuario_id)` e as
 * políticas exigem `usuario_id = auth.uid()` e `empresa_id = any(emp_escrita())` —
 * drenar um cenário que não é o aberto não atravessa incorporadora.
 */
const fila = new Map<string, ItemFila>();
let temporizador: ReturnType<typeof setTimeout> | null = null;
/** Instante da primeira edição da rajada atual — base do teto de adiamento. */
let rajadaIniciadaEm = 0;
/** Portão de concorrência: enquanto true, nenhuma outra gravação começa. */
let emVoo = false;
/** Cenários cujo rascunho o usuário mandou descartar e o servidor ainda não confirmou. */
const exclusoesPendentes = new Set<string>();
/** Cenários que sabemos ter linha gravada — responde `temRascunhoNaoPublicado` offline. */
const comLinhaGravada = new Set<string>();

let iniciado = false;
let cancelarReconexao: (() => void) | null = null;
/** Cresce a cada `iniciarRascunho` — ver lá. */
let geracaoDaSessao = 0;
/** O cenário do `iniciarRascunho` mais recente — já concluído ou ainda esperando o servidor. */
let sessaoPedida: string | null = null;

export function situacaoRascunho(): SituacaoRascunho {
  return {
    cenarioId: sessao?.cenarioId ?? null,
    pendente: fila.size > 0,
    conexao: conexao.estado(),
  };
}

function marcarPendencias(): void {
  conexao.definirPendencias(fila.size === 0 && exclusoesPendentes.size === 0 ? 0 : 1);
}

function estaOnline(): boolean {
  return conexao.estado().rede === 'online';
}

/* =============================================================================
 * Acesso ao banco
 * ========================================================================== */

type Resposta = { data: unknown; error: unknown };

/* O tipo de resposta do Supabase é uma UNIÃO — `{data:T;error:null}` ou
   `{data:null;error:PostgrestError}`. Inferir `T` diretamente dela faz o
   TypeScript colapsar para `never` (ele tenta casar com o ramo do erro, onde
   `data` é `null`). Capturando a resposta INTEIRA em `R` e devolvendo
   `R['data']`, a nulidade é preservada e o estreitamento no chamador funciona. */
async function pedir<R extends Resposta>(consulta: PromiseLike<R>, operacao: string): Promise<R['data']> {
  let r: R;
  try {
    r = await consulta;
  } catch (e) {
    throw traduzirErro(e, operacao);
  }
  if (r.error !== null && r.error !== undefined) throw traduzirErro(r.error, operacao);
  return r.data;
}

/** O cliente já nasce apontado para o schema `app`. */
function app() {
  return obterCliente();
}

async function meuId(): Promise<string> {
  const perfil = await obterPerfil();
  if (perfil === null) {
    throw new ErroFincast('NAO_AUTENTICADO', 'Sua sessão expirou. Entre de novo para continuar.', {
      operacao: 'rascunho',
    });
  }
  return perfil.id;
}

/**
 * Grava o documento.
 *
 * UPDATE-e-depois-INSERT, não `upsert`: o `grant update` de `app.cenario_rascunho` cobre
 * só `conteudo`, `base_versao` e `atualizado_em`. Um upsert do PostgREST monta
 * `ON CONFLICT DO UPDATE SET cenario_id = excluded.cenario_id, …` e morre com
 * "permission denied for column cenario_id". O caminho abaixo escreve exatamente as
 * colunas que o banco deixa escrever.
 */
async function gravarDocumento(s: Sessao, entrada: EntradaMotor): Promise<void> {
  const usuarioId = await meuId();
  const documento: Documento = { formato: FORMATO, entrada };
  const conteudo = documento as unknown as Json;
  const agora = new Date().toISOString();

  const atualizadas = await pedir(
    app()
      .from(TABELA)
      .update({ conteudo, base_versao: s.baseVersao, atualizado_em: agora })
      .eq('cenario_id', s.cenarioId)
      .eq('usuario_id', usuarioId)
      .select('cenario_id'),
    'rascunho',
  );
  if (atualizadas !== null && atualizadas.length > 0) {
    comLinhaGravada.add(s.cenarioId);
    return;
  }

  try {
    await pedir(
      app()
        .from(TABELA)
        .insert({
          cenario_id: s.cenarioId,
          // `empresa_id` não é palavra da tela: veio de `abrirCenario()` (ou de um SELECT
          // sob RLS) e a FK composta (cenario_id, empresa_id) → cenario(id, empresa_id)
          // recusa qualquer par que não exista. Mais a RLS, que exige `emp_escrita()`.
          empresa_id: s.empresaId,
          usuario_id: usuarioId,
          conteudo,
          base_versao: s.baseVersao,
          atualizado_em: agora,
        })
        .select('cenario_id')
        .single(),
      'rascunho',
    );
  } catch (e) {
    /* O CENÁRIO NÃO EXISTE MAIS (excluído de vez pelo Admin Hype, 0028): o UPDATE não achou
       linha (a exclusão apaga os rascunhos) e o INSERT bate na FK composta → cenário. A FK é
       conferida SEM a RLS, então isto só dispara quando o cenário sumiu de verdade — o
       ARQUIVADO continua aceitando rascunho (e o rascunho volta na restauração). Erro próprio,
       para `descarregar` tirar o item da fila em vez de ficar preso nele para sempre. */
    if (cenarioFoiExcluido(e)) {
      throw new ErroFincast('CENARIO_INEXISTENTE', 'Este cenário foi excluído: o que foi digitado nele não pôde ser guardado.', {
        operacao: 'rascunho',
        detalhe: 'fk',
        cenarioId: s.cenarioId,
        erro: e,
      });
    }
    // Outra aba criou a linha entre o UPDATE e o INSERT: uma volta e pronto.
    const chaveDuplicada =
      e instanceof ErroFincast &&
      (e.codigo === 'NOME_DUPLICADO' || /duplicate key|23505/i.test(JSON.stringify(e.causa ?? '')));
    if (!chaveDuplicada) throw e;

    await pedir(
      app()
        .from(TABELA)
        .update({ conteudo, base_versao: s.baseVersao, atualizado_em: agora })
        .eq('cenario_id', s.cenarioId)
        .eq('usuario_id', usuarioId)
        .select('cenario_id'),
      'rascunho',
    );
  }
  comLinhaGravada.add(s.cenarioId);
}

/** O INSERT do rascunho bateu na FK (cenario_id, empresa_id) → cenário: o cenário não existe mais. */
function cenarioFoiExcluido(e: unknown): boolean {
  if (!(e instanceof ErroFincast)) return false;
  const bruto = JSON.stringify(e.causa ?? '');
  return /23503/.test(bruto) && /cenario_rascunho_cenario_id_empresa_id_fkey/.test(bruto);
}

/**
 * Quem a tela avisa quando o que foi digitado num cenário EXCLUÍDO teve de ser descartado
 * (uma vez por cenário). Este módulo não fala com a interface — a tela registra o ouvinte.
 */
let aoDescartarPorExclusao: ((cenarioId: string) => void) | null = null;
const avisadosDaExclusao = new Set<string>();

export function aoDescartarRascunhoDeCenarioExcluido(ouvinte: ((cenarioId: string) => void) | null): void {
  aoDescartarPorExclusao = ouvinte;
}

async function apagarDocumento(cenarioId: string): Promise<void> {
  const usuarioId = await meuId();
  await pedir(
    app().from(TABELA).delete().eq('cenario_id', cenarioId).eq('usuario_id', usuarioId).select('cenario_id'),
    'descartar rascunho',
  );
  comLinhaGravada.delete(cenarioId);
}

/* =============================================================================
 * Descarga: uma gravação por vez; a retentativa é do monitor de conexão
 * ========================================================================== */

function agendar(atrasoMs: number): void {
  if (temporizador !== null) clearTimeout(temporizador);
  temporizador = setTimeout(() => {
    temporizador = null;
    void tentar();
  }, atrasoMs);
}

function cancelarAgendamento(): void {
  if (temporizador !== null) {
    clearTimeout(temporizador);
    temporizador = null;
  }
}

/**
 * Esvazia a fila. **Lança** quando falha — é assim que `conexao.aoReconectar` sabe que
 * ainda há pendência e reagenda. Só uma execução por vez: quem chega no meio deposita
 * em `pendente` e vai embora; o laço de quem está gravando recolhe.
 */
async function descarregar(): Promise<void> {
  /* Sair em silêncio aqui era lido como SUCESSO por `sincronizarAgora`, que então
     anunciava "Salvo às 10:15" com o conteúdo em lugar nenhum. Se há trabalho na
     fila e não podemos drenar agora, isto tem de LANÇAR. */
  if (emVoo) {
    if (fila.size > 0 || exclusoesPendentes.size > 0) {
      throw new ErroFincast('GRAVACAO_EM_ANDAMENTO', 'Ainda estamos salvando o que você digitou.', {
        operacao: 'rascunho',
        detalhe: 'outra gravação em voo',
      });
    }
    return;
  }
  emVoo = true;
  try {
    for (;;) {
      const exclusao = [...exclusoesPendentes][0];
      if (exclusao !== undefined) {
        // Descarte antes de gravação: quem mandou apagar não quer o conteúdo de volta.
        await conexao.rastrearSalvamento(() => apagarDocumento(exclusao));
        exclusoesPendentes.delete(exclusao);
        marcarPendencias();
        continue;
      }

      /* O item mais antigo primeiro. Não depende de `sessao`: cada item carrega a
         sua empresa e versão-base, então a fila drena mesmo depois de trocar de
         cenário ou de encerrar a sessão de rascunho. */
      const proximo = fila.entries().next();
      if (proximo.done === true) return;
      const [cenarioId, item] = proximo.value;

      /* NÃO removemos antes de gravar. A remoção só acontece com a confirmação do
         servidor — era exatamente o `pendente = null` otimista que perdia trabalho
         e trocava o conteúdo de um cenário pelo de outro.

         A EXCEÇÃO é o cenário que não existe mais (09/10/2026, revisão do lote): o item dele
         nunca vai gravar, e como a fila drena o mais antigo PRIMEIRO, ele ficava na cabeça
         para sempre — nada dos outros cenários da sessão subia até recarregar a página, e a
         barra dizia "Não foi possível salvar" sem dizer de qual. Agora ele sai da fila, a
         tela é avisada uma vez, e o laço segue para o próximo. */
      let excluido = false;
      await conexao.rastrearSalvamento(async () => {
        try {
          await gravarDocumento(item, item.entrada);
        } catch (e) {
          if (e instanceof ErroFincast && e.codigo === 'CENARIO_INEXISTENTE') {
            excluido = true;
            return;
          }
          throw e;
        }
      });
      if (excluido) {
        if (fila.get(cenarioId) === item) fila.delete(cenarioId);
        comLinhaGravada.delete(cenarioId);
        if (fila.size === 0) rajadaIniciadaEm = 0;
        marcarPendencias();
        if (!avisadosDaExclusao.has(cenarioId)) {
          avisadosDaExclusao.add(cenarioId);
          try { aoDescartarPorExclusao?.(cenarioId); } catch { /* aviso é da tela; não derruba a fila */ }
        }
        continue;
      }

      /* Se o usuário digitou mais nesse mesmo cenário durante a gravação, o item foi
         SUBSTITUÍDO por um mais novo — apagar aqui descartaria a edição nova. */
      if (fila.get(cenarioId) === item) fila.delete(cenarioId);
      if (fila.size === 0) rajadaIniciadaEm = 0;
      marcarPendencias();
    }
  } finally {
    emVoo = false;
  }
}

/** Versão que não lança: para o temporizador e para quem chama da interface. */
async function tentar(): Promise<void> {
  try {
    await descarregar();
  } catch {
    // O monitor já classificou (pendente = tenta de novo · erro = precisa de decisão)
    // e a frase de tela saiu por lá. Aqui não há nada a acrescentar.
  }
}

/** Liga a retomada automática e o descarregamento ao esconder a aba. Uma vez por processo. */
function garantirIniciado(): void {
  if (iniciado) return;
  iniciado = true;

  // Quando a rede volta (e a cada retentativa agendada), o monitor chama isto sozinho.
  cancelarReconexao = conexao.aoReconectar(descarregar);

  /* Trocar de usuário no mesmo navegador NÃO pode levar a fila do anterior: o item
     seria gravado com `usuario_id = auth.uid()` do novo, ou seja o trabalho de um
     apareceria como rascunho do outro. A fila é local e não confirmada — perder o
     que não foi gravado é o comportamento certo aqui. */
  aoMudarAutenticacao((evento) => {
    if (evento === 'SIGNED_OUT' || evento === 'USER_UPDATED' || evento === 'SIGNED_IN') {
      fila.clear();
      exclusoesPendentes.clear();
      comLinhaGravada.clear();
      sessao = null;
      rajadaIniciadaEm = 0;
      cancelarAgendamento();
      marcarPendencias();
    }
  });

  // Aba escondida (trocou de janela, fechou o notebook) descarrega antes de dormir.
  // Acesso por `globalThis` porque o tsconfig não carrega a lib "DOM".
  const doc = (globalThis as unknown as { document?: DocumentoMinimo }).document;
  if (doc && typeof doc.addEventListener === 'function') {
    doc.addEventListener('visibilitychange', () => {
      if (doc.visibilityState === 'hidden' && fila.size > 0) {
        cancelarAgendamento();
        void tentar();
      }
    });
  }
}

/* =============================================================================
 * API
 * ========================================================================== */

/**
 * Abre a sessão de rascunho de um cenário. Chamar logo depois de `abrirCenario()`.
 *
 * `empresaId` e `baseVersao` vêm do `CenarioResumo`; se não vierem, são lidos do próprio
 * cenário (sob RLS) para nunca dependerem do que a tela informou.
 */
export async function iniciarRascunho(args: {
  cenarioId: string;
  empresaId?: string;
  baseVersao?: number;
}): Promise<void> {
  garantirIniciado();
  /* A ÚLTIMA chamada manda (09/10/2026, revisão do lote). Esta função espera o servidor no
     meio; duas telas abrindo quase juntas (trocar e voltar rápido) terminavam fora de ordem, e
     a sessão ficava sendo a da tela que já tinha saído — as edições da tela visível viravam
     rascunho do OUTRO cenário. Quem chegou antes e terminou depois desiste. */
  const minha = ++geracaoDaSessao;
  sessaoPedida = args.cenarioId;

  let empresaId: string;
  let baseVersao: number;
  if (args.empresaId === undefined || args.baseVersao === undefined) {
    const linha = await pedir(
      app().from('cenario').select('empresa_id, versao').eq('id', args.cenarioId).single(),
      'cenario',
    );
    if (linha === null) {
      throw new ErroFincast(
        'CENARIO_INEXISTENTE',
        'Cenário não encontrado — ou você não tem permissão para vê-lo.',
        { operacao: 'rascunho', cenarioId: args.cenarioId },
      );
    }
    empresaId = args.empresaId ?? linha.empresa_id;
    baseVersao = args.baseVersao ?? linha.versao;
  } else {
    empresaId = args.empresaId;
    baseVersao = args.baseVersao;
  }

  /* Tenta subir o que está pendente antes de trocar — melhor para o usuário, que vê
     "salvo" mais cedo. Mas NÃO é obrigatório e NÃO limpa a fila: cada item é
     autossuficiente e continua drenável depois da troca. Antes, esta troca (e até
     reabrir o MESMO cenário) descartava o pendente sem ir ao servidor. */
  if (fila.size > 0 && estaOnline()) await tentar();

  if (minha !== geracaoDaSessao) return;
  sessao = { cenarioId: args.cenarioId, empresaId, baseVersao };
  rajadaIniciadaEm = 0;
  cancelarAgendamento();
  marcarPendencias();
}

/**
 * Registra uma edição. É o que a tela chama a cada tecla, cada célula colada, cada
 * checkbox — de graça: não vai ao servidor, só reagenda o agrupamento.
 *
 * A EDIÇÃO DIZ DE QUAL CENÁRIO ELA É, e só entra na sessão DESSE cenário (09/10/2026, revisão
 * do lote). Antes ela entrava na sessão que estivesse aberta, fosse qual fosse — e a sessão
 * nunca era encerrada: o Admin Hype que abria um cenário da própria incorporadora (editável,
 * abre sessão) e depois, na mesma aba, um de cliente (só-leitura, não abre) continuava com a
 * sessão do primeiro, e o `persist()` que o simulador chama ao abrir/fechar um painel gravava a
 * entrada do SEGUNDO no rascunho do PRIMEIRO. Reproduzido no navegador: a volta ao primeiro
 * dizia "Recuperamos o que você tinha digitado" e mostrava os números do outro. Sem sessão
 * deste cenário (tela só-leitura, ou tela que já saiu) a edição é RECUSADA — devolve `false`.
 */
export function anotarEdicao(cenarioId: string, entrada: EntradaMotor): boolean {
  if (sessao === null || sessao.cenarioId !== cenarioId) return false;
  garantirIniciado();

  // Voltou a editar depois de mandar descartar: a edição manda.
  exclusoesPendentes.delete(sessao.cenarioId);

  fila.set(sessao.cenarioId, { ...sessao, entrada });
  const agora = Date.now();
  if (rajadaIniciadaEm === 0) rajadaIniciadaEm = agora;
  marcarPendencias();

  // Offline: não adianta tentar. O monitor já está com a retentativa agendada e
  // chama `descarregar` sozinho quando a rede voltar.
  if (!estaOnline()) {
    cancelarAgendamento();
    return true;
  }

  // Digitação contínua não adia a gravação para sempre.
  if (agora - rajadaIniciadaEm >= ADIAMENTO_MAXIMO_MS) {
    cancelarAgendamento();
    void tentar();
    return true;
  }
  agendar(ESPERA_MS);
  return true;
}

/**
 * Força a gravação do que está pendente e espera terminar. Usar antes de publicar, de
 * trocar de cenário ou de fechar a tela. Não lança: a situação (salvo / pendente / erro)
 * vem no retorno, que é o mesmo estado que a barra de status mostra.
 */
export async function gravarAgora(): Promise<SituacaoRascunho> {
  cancelarAgendamento();
  await tentar();
  // Uma gravação que já estava no ar quando chegamos: espera drenar e repete.
  while (emVoo) await new Promise<void>((r) => setTimeout(r, 25));
  if ((fila.size > 0 || exclusoesPendentes.size > 0) && estaOnline()) await tentar();
  return situacaoRascunho();
}

/**
 * Lê o rascunho pessoal do cenário. `null` quando não há — **e também quando está
 * vencido**.
 *
 * Rascunho VENCIDO é aquele cuja `base_versao` é menor que a `versao` corrente do
 * cenário: alguém (outra aba, outra pessoa) publicou depois que este rascunho foi
 * escrito. Oferecê-lo como "alterações não publicadas" é perigoso, e não em teoria:
 * aceitar e salvar REGRIDE a versão publicada, e a trava otimista não protege, porque
 * a versão que `abrirCenario` traz é a corrente. A coluna `base_versao` já existia e
 * era escrita, mas nunca comparada com nada.
 *
 * `versaoDaTela` (CAL-3, revisão de 11/10/2026): a versão que a TELA abriu. Com ela, "vencido" é
 * `base_versao < versaoDaTela` — a mesma régua de `temRascunhoNaoPublicado`. Contra a versão do
 * BANCO, uma publicação de outra pessoa ENTRE o `abrirCenario` e esta leitura escondia o
 * rascunho feito sobre a versão que está na tela, e a 1ª edição o sobrescrevia. Sem o argumento,
 * a régua antiga (contra o banco).
 */
export async function carregarRascunho(cenarioId?: string, versaoDaTela?: number): Promise<RascunhoCarregado | null> {
  const alvo = cenarioId ?? sessao?.cenarioId;
  if (!alvo) return null;
  const usuarioId = await meuId();

  const linha = await pedir(
    app()
      .from(TABELA)
      .select('cenario_id, conteudo, base_versao, atualizado_em')
      .eq('cenario_id', alvo)
      .eq('usuario_id', usuarioId)
      .maybeSingle(),
    'rascunho',
  );
  if (linha === null) {
    comLinhaGravada.delete(alvo);
    return null;
  }
  comLinhaGravada.add(alvo);

  const vencido = versaoDaTela !== undefined ? linha.base_versao < versaoDaTela : await estaVencido(alvo, linha.base_versao);
  if (vencido) {
    comLinhaGravada.delete(alvo);
    return null;
  }

  const doc = linha.conteudo as unknown as Partial<DocumentoLido> | null;
  if (!doc || (doc.formato !== FORMATO && doc.formato !== FORMATO_1) || !doc.entrada) {
    // Documento de um formato que não conhecemos: não adivinhar, não apagar.
    throw new ErroFincast(
      'RASCUNHO_INCOMPATIVEL',
      'O rascunho guardado está em um formato que esta versão não lê. Abra o cenário publicado e refaça as alterações.',
      { operacao: 'rascunho', cenarioId: alvo },
    );
  }

  const formato: 1 | 2 = doc.formato === FORMATO_1 ? 1 : 2;
  return {
    cenarioId: alvo,
    entrada: doc.entrada,
    baseVersao: linha.base_versao,
    atualizadoEm: linha.atualizado_em,
    formato,
    /* a origem do LGC só vale no formato 1 (D25 = A) — ver `RascunhoCarregado` */
    ...(formato === 1 && numeroFinito(doc.lgcOficialNaOrigem) ? { lgcOficialNaOrigem: doc.lgcOficialNaOrigem } : {}),
  };
}

/**
 * Há trabalho do usuário que ainda não virou versão publicada?
 *
 * Conta como "não publicado": edição em memória esperando gravação, e linha em
 * `app.cenario_rascunho`. Sem conexão, responde pelo que já sabe — não trava a tela
 * esperando o servidor.
 */
export async function temRascunhoNaoPublicado(cenarioId?: string, versaoDaTela?: number): Promise<boolean> {
  /* `versaoDaTela` (revisão do lote 4, 10/10/2026 — defeito ANTERIOR ao lote): a sonda do EL-190 age
     exatamente quando o banco passou da versão que está na tela. Julgar "vencido" contra a versão do
     BANCO dava todo rascunho já gravado sobre a versão da tela como vencido — a guarda só segurava
     enquanto a edição estava na fila (~800 ms) e a publicação do outro trocava a tela, apagando o que
     foi digitado (regra dura 9). Com a versão da tela, o rascunho feito sobre ela É o trabalho que está
     na tela; um mais antigo (não mostrado) continua não contando. Sem o argumento, a régua de antes
     (a oferta do rascunho na abertura). */
  const alvo = cenarioId ?? sessao?.cenarioId;
  if (!alvo) return false;

  if (fila.has(alvo)) return true;
  if (exclusoesPendentes.has(alvo)) return false;
  if (!estaOnline()) return comLinhaGravada.has(alvo);
  /* Rascunho vencido (alguém publicou depois dele) não é "alteração não publicada" —
     ver `carregarRascunho`. Oferecer levaria a regredir a versão publicada. */

  const usuarioId = await meuId();
  const linha = await pedir(
    app().from(TABELA).select('cenario_id, base_versao').eq('cenario_id', alvo).eq('usuario_id', usuarioId).maybeSingle(),
    'rascunho',
  );
  if (linha === null) {
    comLinhaGravada.delete(alvo);
    return false;
  }
  const vencido = versaoDaTela !== undefined ? linha.base_versao < versaoDaTela : await estaVencido(alvo, linha.base_versao);
  if (vencido) {
    comLinhaGravada.delete(alvo);
    return false;
  }
  comLinhaGravada.add(alvo);
  return true;
}

/**
 * Quais destes cenários têm alteração MINHA ainda não publicada — o selo do seletor
 * (09/10/2026). Uma leitura só para a lista inteira, com a mesma régua de
 * `temRascunhoNaoPublicado`: edição na fila conta, descarte pendente não conta, e rascunho
 * VENCIDO (alguém publicou depois dele) não conta. A versão de cada cenário vem de quem
 * chama — é a da lista que a tela já tem, e evita uma segunda ida ao servidor.
 *
 * Antes o selo "com alterações" saía de `versao − versao_publicada`, que é SEMPRE 1 num
 * cenário publicado (salvar soma 1 e publicar soma 1): todo cenário publicado aparecia
 * "com alterações". O que diz se há trabalho não publicado é o rascunho — e ele é por pessoa.
 */
export async function cenariosComRascunho(
  cenarios: ReadonlyArray<{ id: string; versao: number }>,
): Promise<Set<string>> {
  const saida = new Set<string>();
  if (cenarios.length === 0) return saida;
  const versao = new Map(cenarios.map((c) => [c.id, c.versao]));
  for (const c of cenarios) if (fila.has(c.id)) saida.add(c.id);
  if (!estaOnline()) {
    for (const c of cenarios) if (comLinhaGravada.has(c.id) && !exclusoesPendentes.has(c.id)) saida.add(c.id);
    return saida;
  }
  const usuarioId = await meuId();
  const linhas = await pedir(
    app().from(TABELA).select('cenario_id, base_versao').eq('usuario_id', usuarioId).in('cenario_id', [...versao.keys()]),
    'rascunho',
  );
  for (const l of linhas ?? []) {
    if (exclusoesPendentes.has(l.cenario_id)) continue;
    const v = versao.get(l.cenario_id);
    if (v !== undefined && l.base_versao < v) continue; // vencido: oferecê-lo regrediria a versão publicada
    saida.add(l.cenario_id);
  }
  return saida;
}

/**
 * `true` quando o cenário já foi publicado DEPOIS deste rascunho.
 *
 * Em caso de dúvida (não conseguimos ler a versão corrente) responde `false`: preferimos
 * mostrar um rascunho possivelmente velho — que o usuário reconhece e descarta — a
 * esconder trabalho que talvez seja o único que existe.
 */
async function estaVencido(cenarioId: string, baseVersao: number): Promise<boolean> {
  try {
    const cen = await pedir(
      app().from('cenario').select('versao').eq('id', cenarioId).maybeSingle(),
      'cenario',
    );
    if (cen === null) return false;
    return baseVersao < cen.versao;
  } catch {
    return false;
  }
}

/**
 * Joga fora o rascunho pessoal: memória e linha no servidor.
 *
 * Sem conexão, a memória é limpa na hora e o apagamento entra na fila — senão o rascunho
 * descartado ressuscitaria no próximo reconectar. Chamar depois de publicar (o rascunho
 * virou passado) e no "descartar alterações" da tela.
 */
export async function descartarRascunho(cenarioId?: string): Promise<void> {
  const alvo = cenarioId ?? sessao?.cenarioId;
  if (!alvo) return;

  garantirIniciado();
  /* Descartar é por CENÁRIO, não "o que estiver aberto": tira da fila o item daquele
     cenário e deixa os outros em paz. Antes, com uma variável só, descartar o cenário
     A também jogava fora o que estava pendente de B. */
  fila.delete(alvo);
  if (fila.size === 0) rajadaIniciadaEm = 0;
  if (sessao?.cenarioId === alvo) cancelarAgendamento();
  exclusoesPendentes.add(alvo);
  marcarPendencias();

  if (!estaOnline()) return; // sobe na volta da conexão, pelo `aoReconectar`

  /* CAL-4 (revisão de 11/10/2026): com uma gravação NO AR, apagar agora correria com ela — ela
     terminava DEPOIS do delete e recriava a linha. Espera terminar: o laço de quem está gravando
     drena as exclusões ANTES das gravações (`descarregar`), na ordem, e costuma já apagar esta. */
  while (emVoo) await new Promise<void>((r) => setTimeout(r, 25));
  if (!exclusoesPendentes.has(alvo)) return;   // a fila já apagou (ou a pessoa voltou a editar)

  try {
    await conexao.rastrearSalvamento(() => apagarDocumento(alvo));
    exclusoesPendentes.delete(alvo);
    marcarPendencias();
  } catch (e) {
    const traduzido = traduzirErro(e, 'descartar rascunho');
    // Falha de rede fica na fila em silêncio; o resto o usuário precisa saber.
    if (traduzido.codigo !== 'SEM_CONEXAO') throw traduzido;
  }
}

/**
 * Atualiza a versão-base depois de publicar: o próximo rascunho passa a se referir à
 * versão nova. Não grava nada sozinho. Só mexe na sessão DESTE cenário — a mesma régua de
 * `anotarEdicao`: a versão de um cenário nunca vai parar na sessão de outro.
 * (Sem o LGC desde a 0032: o oficial só muda publicando ESTE cenário, e o formato 2 não guarda origem.)
 */
export function atualizarVersaoBase(cenarioId: string, baseVersao: number): void {
  if (sessao !== null && sessao.cenarioId === cenarioId) {
    sessao = { ...sessao, baseVersao };
  }
}

/**
 * APC-3 (revisão de 12/10/2026): regrava o rascunho de um cenário cuja tela JÁ SAIU, sobre a
 * versão-base nova. É o caso da publicação que volta depois de a pessoa sair do cenário: o que
 * ela digitou durante a ida (1 a 2 s) foi anotado com a versão-base VELHA, e a próxima abertura o
 * esconderia como vencido. Sem sessão do cenário, `anotarEdicao` recusa — aqui o item entra na
 * fila com a empresa e a versão dele (cada item é autossuficiente; ver a `fila`) e sobe sozinho.
 *
 * RECUSA (devolve `false`) quando a sessão aberta é a DESTE cenário: aí a tela dele voltou a
 * existir, e é ela quem manda no rascunho — a mesma régua de `anotarEdicao`, ao contrário.
 */
export function regravarForaDaSessao(item: {
  cenarioId: string;
  empresaId: string;
  baseVersao: number;
  entrada: EntradaMotor;
}): boolean {
  if (sessao !== null && sessao.cenarioId === item.cenarioId) return false;
  garantirIniciado();
  // Como numa edição: mandar regravar depois de mandar descartar — a regravação manda.
  exclusoesPendentes.delete(item.cenarioId);
  fila.set(item.cenarioId, { cenarioId: item.cenarioId, empresaId: item.empresaId, baseVersao: item.baseVersao, entrada: item.entrada });
  if (rajadaIniciadaEm === 0) rajadaIniciadaEm = Date.now();
  marcarPendencias();
  /* Offline, o monitor de conexão já drena a fila na volta. Com uma gravação no ar, o laço dela
     pega este item (ele substituiu o antigo na fila, e o antigo não é removido por cima dele). */
  if (estaOnline()) void tentar();
  return true;
}

/**
 * Fecha a sessão do cenário — é o `aoSair` da tela do cenário que chama (09/10/2026; antes
 * ninguém chamava, e a sessão do último cenário editável ficava aberta pelas telas seguintes).
 *
 * Com `cenarioId`, só fecha o que é DESSE cenário: a sessão dele, e o `iniciarRascunho` dele
 * que ainda esteja esperando o servidor (a geração sobe e ele desiste ao voltar — senão a tela
 * que já saiu abriria a sessão DEPOIS de sair). A sessão de outro cenário fica como está.
 *
 * Tenta descarregar o que estiver pendente, mas não bloqueia: o que não subir continua na
 * fila e sobe quando a conexão voltar, enquanto a página viver.
 */
export function encerrarRascunho(cenarioId?: string): void {
  const daSessao = sessao !== null && (cenarioId === undefined || sessao.cenarioId === cenarioId);
  const doPedido = sessaoPedida !== null && (cenarioId === undefined || sessaoPedida === cenarioId);
  if (!daSessao && !doPedido) return;
  if (doPedido) {
    geracaoDaSessao++;
    sessaoPedida = null;
  }
  if (daSessao) sessao = null;
  cancelarAgendamento();
  if (fila.size > 0 && estaOnline()) void tentar();   /* a fila NÃO é limpa: cada item é autossuficiente e continua drenável */
}

/** Só para teste: derruba tudo, inclusive a inscrição no monitor de conexão. */
export function reiniciarRascunhoParaTeste(): void {
  cancelarAgendamento();
  if (cancelarReconexao) cancelarReconexao();
  cancelarReconexao = null;
  iniciado = false;
  sessao = null;
  sessaoPedida = null;
  fila.clear();
  emVoo = false;
  rajadaIniciadaEm = 0;
  exclusoesPendentes.clear();
  comLinhaGravada.clear();
  avisadosDaExclusao.clear();
  aoDescartarPorExclusao = null;
  conexao.definirPendencias(0);
}
